/**
 * Dev Dashboard — Unified developer tools page.
 *
 * Replaces three floating DevTools panels (TenantSwitch, ActorSwitch, IndustrySwitch)
 * with a proper dashboard page at /dev.
 *
 * Sections:
 *   1. System Health — Gateway, Valkey Cache, Valkey State, DynamoDB
 *   2. Tenant Selector — All 16 tenants from backend + metadata
 *   3. Actor Selector — Actors for current tenant from GraphQL
 *   4. Vertical Roadmap — 5 verticals from verticals config
 *   5. Config Inventory — 15 config types with seeded counts
 *   6. Quick Actions — Architect tools, view layer, environment info
 *
 * Dev mode only — returns null if not in dev mode.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Title,
  Text,
  SimpleGrid,
  Card,
  Group,
  Stack,
  Badge,
  Table,
  Select,
  Button,
  Divider,
  Alert,
  ThemeIcon,
  Loader,
  Tooltip,
} from '@mantine/core';
import {
  IconHeartbeat,
  IconServer,
  IconDatabase,
  IconUsers,
  IconBuilding,
  IconMap,
  IconSettings,
  IconRocket,
  IconRefresh,
  IconAlertCircle,
  IconCheck,
  IconX,
  IconTopologyStar3,
  IconLayoutDashboard,
  IconEye,
  IconShieldCheck,
} from '@tabler/icons-react';

import { isDevModeEnabled } from '@/utils/env';
import { useAuth, useViewLayer } from '@/auth/AuthContext';
import { useTenantId } from '@/auth';
import { useListActorsQuery } from '@/generated/graphql';
import { useNavigate } from 'react-router-dom';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DevTenant {
  tenant_id: string;
  tenant_num: number;
  display_name: string;
  tier: string;
  industry?: string;
  subgraph_ids: number[];
}

interface HealthStatus {
  gateway: boolean | null;
  valkey_cache: boolean | null;
  valkey_state: boolean | null;
  dynamodb: boolean | null;
  storage_backend: string;
}

interface Vertical {
  name: string;
  code: string;
  status: 'in_progress' | 'planned' | 'future';
  priority: number;
  target: string;
}

// ─── Static Data (production tenants from infra/tenants.yaml) ────────────────

const ALL_TENANTS: DevTenant[] = [
  { tenant_id: 'ferp-system', tenant_num: 0, display_name: 'System Tenant', tier: 'system', subgraph_ids: [0, 1, 2, 4] },
  { tenant_id: 'hillside-harmony', tenant_num: 2, display_name: 'Hillside Harmony Assisted Living', tier: 'production', industry: 'assisted_living', subgraph_ids: [0, 1, 2, 4, 8, 9] },
  { tenant_id: 'athanor-alf', tenant_num: 3, display_name: 'Athanor Assisted Living, LLC', tier: 'production', industry: 'assisted_living', subgraph_ids: [0, 1, 2, 4] },
  { tenant_id: 'omniquark', tenant_num: 4, display_name: 'Omniquark, LLC', tier: 'production', industry: 'professional_services', subgraph_ids: [0, 2, 4] },
  { tenant_id: 'cc-kitchen', tenant_num: 5, display_name: 'Culturally Conscious Kitchen', tier: 'production', industry: 'hospitality', subgraph_ids: [0, 4] },
  { tenant_id: 'cc-laundry', tenant_num: 6, display_name: 'Culturally Conscious Laundry', tier: 'production', industry: 'services', subgraph_ids: [0, 4] },
  { tenant_id: 'aahhcs', tenant_num: 7, display_name: 'Asian-American Home Health Care Services', tier: 'production', industry: 'home_care', subgraph_ids: [0, 1, 2, 4] },
  { tenant_id: 'athanor-wellness', tenant_num: 21, display_name: 'Athanor Beauty & Wellness', tier: 'production', industry: 'healthcare', subgraph_ids: [0, 4] },
  { tenant_id: 'athanor-memorial', tenant_num: 22, display_name: 'Athanor Celebration of Life', tier: 'production', industry: 'services', subgraph_ids: [0, 4] },
  { tenant_id: 'phin-personal', tenant_num: 23, display_name: 'Phin Personal Finance', tier: 'production', industry: 'personal', subgraph_ids: [0, 4] },
];

const TIER_COLORS: Record<string, string> = {
  system: 'red',
  demo: 'blue',
  staging: 'yellow',
  production: 'green',
};

const SUBGRAPH_NAMES: Record<number, string> = {
  0: 'core',
  1: 'fhir',
  2: 'payroll',
  4: 'accounting',
  8: 'edi',
  9: 'wearables',
};

const VERTICALS: Vertical[] = [
  { name: 'Assisted Living', code: 'v0-i0', status: 'in_progress', priority: 1, target: 'Independent and chain ALFs, 20-120 beds' },
  { name: 'Home Care', code: 'v0-i1', status: 'planned', priority: 2, target: 'Home care agencies, PCA services' },
  { name: 'Dentistry', code: 'v1-i2', status: 'planned', priority: 3, target: 'Independent dental practices' },
  { name: 'DME', code: 'v0-i5', status: 'future', priority: 4, target: 'DME suppliers, home medical equipment' },
  { name: 'Building Management', code: 'v4-i0', status: 'future', priority: 5, target: 'Healthcare facility maintenance' },
];

const VERTICAL_STATUS_COLORS: Record<string, string> = {
  in_progress: 'blue',
  planned: 'yellow',
  future: 'gray',
};

// CONFIG_TYPES removed — now served from backend via Registry Explorer (DRR-111)
// See: /architect/registries

// ─── Health Check Hook ───────────────────────────────────────────────────────

function useHealthCheck(): { health: HealthStatus; refreshHealth: () => void } {
  const [health, setHealth] = useState<HealthStatus>({
    gateway: null,
    valkey_cache: null,
    valkey_state: null,
    dynamodb: null,
    storage_backend: 'unknown',
  });

  const refreshHealth = useCallback(() => {
    // Gateway liveness
    fetch('/healthz', { signal: AbortSignal.timeout(3000) })
      .then((r) => setHealth((h) => ({ ...h, gateway: r.ok })))
      .catch(() => setHealth((h) => ({ ...h, gateway: false })));

    // Valkey readiness (readyz pings Redis)
    fetch('/readyz', { signal: AbortSignal.timeout(3000) })
      .then((r) => {
        setHealth((h) => ({
          ...h,
          valkey_cache: r.ok,
          valkey_state: r.ok,
        }));
      })
      .catch(() => {
        setHealth((h) => ({
          ...h,
          valkey_cache: false,
          valkey_state: false,
        }));
      });

    // Storage info
    fetch('/health/storage', { signal: AbortSignal.timeout(3000) })
      .then((r) => r.json())
      .then((data: { storage?: string }) => {
        setHealth((h) => ({
          ...h,
          dynamodb: true,
          storage_backend: data.storage ?? 'unknown',
        }));
      })
      .catch(() => setHealth((h) => ({ ...h, dynamodb: false })));
  }, []);

  useEffect(() => {
    refreshHealth();
    const interval = setInterval(refreshHealth, 15000);
    return () => clearInterval(interval);
  }, [refreshHealth]);

  return { health, refreshHealth };
}

// ─── Status Badge ────────────────────────────────────────────────────────────

function StatusBadge({ status, label }: { status: boolean | null; label: string }) {
  if (status === null) {
    return (
      <Group gap="xs">
        <Loader size="xs" />
        <Text size="sm">{label}</Text>
      </Group>
    );
  }
  return (
    <Group gap="xs">
      <ThemeIcon size="sm" radius="xl" color={status ? 'green' : 'red'} variant="filled">
        {status ? <IconCheck size={12} /> : <IconX size={12} />}
      </ThemeIcon>
      <Text size="sm">{label}</Text>
    </Group>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function DevDashboardPage() {
  const navigate = useNavigate();
  const viewLayer = useViewLayer();
  const { user, setViewLayer } = useAuth();
  const tenantId = useTenantId();
  const { health, refreshHealth } = useHealthCheck();

  // Actor query for current tenant
  const { data: actorsData, loading: actorsLoading } = useListActorsQuery({
    variables: { limit: 100 },
  });

  // Dev mode guard
  if (!isDevModeEnabled()) {
    return (
      <Container size="sm" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Dev Mode Required" color="red">
          This page is only available in development mode.
        </Alert>
      </Container>
    );
  }

  const currentTenant = ALL_TENANTS.find((t) => t.tenant_id === tenantId);
  const actors = actorsData?.listActors?.items ?? [];

  const handleTenantSwitch = (newTenantId: string | null) => {
    if (newTenantId) {
      localStorage.setItem('tenant_id', newTenantId);
      window.location.reload();
    }
  };

  const handleActorSwitch = (actorAuthId: string | null) => {
    if (actorAuthId) {
      localStorage.setItem('dev_actor_auth_id', actorAuthId);
      window.location.reload();
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <ThemeIcon size="lg" variant="light" color="violet">
              <IconLayoutDashboard size={20} />
            </ThemeIcon>
            <div>
              <Title order={2}>Dev Dashboard</Title>
              <Text size="sm" c="dimmed">Development tools and system status</Text>
            </div>
          </Group>
          <Badge color="violet" variant="light" size="lg">DEV MODE</Badge>
        </Group>

        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
          {/* ── 1. System Health ── */}
          <Card withBorder p="md">
            <Group justify="space-between" mb="sm">
              <Group gap="xs">
                <IconHeartbeat size={18} />
                <Text fw={600}>System Health</Text>
              </Group>
              <Tooltip label="Refresh health checks">
                <Button variant="subtle" size="xs" onClick={refreshHealth} leftSection={<IconRefresh size={14} />}>
                  Refresh
                </Button>
              </Tooltip>
            </Group>
            <Stack gap="xs">
              <StatusBadge status={health.gateway} label="Gateway" />
              <StatusBadge status={health.valkey_cache} label="Valkey Cache (port 6379)" />
              <StatusBadge status={health.valkey_state} label="Valkey State (port 6380)" />
              <StatusBadge status={health.dynamodb} label={`DynamoDB (${health.storage_backend})`} />
            </Stack>
          </Card>

          {/* ── 2. Tenant Selector ── */}
          <Card withBorder p="md">
            <Group gap="xs" mb="sm">
              <IconBuilding size={18} />
              <Text fw={600}>Tenant</Text>
              {currentTenant && (
                <Badge color={TIER_COLORS[currentTenant.tier] ?? 'gray'} size="sm" variant="light">
                  {currentTenant.tier}
                </Badge>
              )}
            </Group>
            {currentTenant && (
              <Stack gap="xs" mb="sm">
                <Text size="sm"><strong>{currentTenant.display_name}</strong> (#{currentTenant.tenant_num})</Text>
                <Group gap="xs">
                  {currentTenant.industry && <Badge size="xs" variant="dot">{currentTenant.industry}</Badge>}
                  {currentTenant.subgraph_ids.map((id) => (
                    <Badge key={id} size="xs" variant="outline">{SUBGRAPH_NAMES[id] ?? `sg-${id}`}</Badge>
                  ))}
                </Group>
              </Stack>
            )}
            <Select
              placeholder="Switch tenant..."
              data={ALL_TENANTS.map((t) => ({
                value: t.tenant_id,
                label: `${t.display_name} (#${t.tenant_num})`,
              }))}
              value={tenantId}
              onChange={handleTenantSwitch}
              searchable
              size="sm"
            />
          </Card>

          {/* ── 3. Actor Selector ── */}
          <Card withBorder p="md">
            <Group gap="xs" mb="sm">
              <IconUsers size={18} />
              <Text fw={600}>Actor</Text>
              {user && <Badge size="sm" variant="light">{user.displayName ?? user.email}</Badge>}
            </Group>
            {actorsLoading ? (
              <Group gap="xs"><Loader size="xs" /><Text size="sm">Loading actors...</Text></Group>
            ) : actors.length === 0 ? (
              <Text size="sm" c="dimmed">No actors found for this tenant.</Text>
            ) : (
              <Select
                placeholder="Switch actor..."
                data={actors.map((a) => ({
                  value: a.id,
                  label: a.name ?? a.id,
                }))}
                onChange={handleActorSwitch}
                searchable
                size="sm"
              />
            )}
            <Divider my="sm" />
            <Group gap="xs">
              <Text size="xs" c="dimmed">View Layer:</Text>
              {(['user', 'admin', 'architect'] as const).map((layer) => (
                <Badge
                  key={layer}
                  size="sm"
                  variant={viewLayer === layer ? 'filled' : 'light'}
                  color={layer === 'architect' ? 'violet' : layer === 'admin' ? 'orange' : 'blue'}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setViewLayer(layer)}
                >
                  {layer}
                </Badge>
              ))}
            </Group>
          </Card>

          {/* ── 4. Vertical Roadmap ── */}
          <Card withBorder p="md">
            <Group gap="xs" mb="sm">
              <IconMap size={18} />
              <Text fw={600}>Vertical Roadmap</Text>
            </Group>
            <Table striped highlightOnHover withTableBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Vertical</Table.Th>
                  <Table.Th>Code</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>P</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {VERTICALS.map((v) => (
                  <Table.Tr key={v.code} style={
                    currentTenant?.industry && v.name.toLowerCase().includes(currentTenant.industry.replace('_', ' '))
                      ? { backgroundColor: 'var(--mantine-color-blue-light)' }
                      : undefined
                  }>
                    <Table.Td>
                      <Tooltip label={v.target}>
                        <Text size="sm">{v.name}</Text>
                      </Tooltip>
                    </Table.Td>
                    <Table.Td><Text size="xs" ff="monospace">{v.code}</Text></Table.Td>
                    <Table.Td>
                      <Badge size="xs" color={VERTICAL_STATUS_COLORS[v.status]}>{v.status.replace('_', ' ')}</Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs">{v.priority}</Text></Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        </SimpleGrid>

        {/* ── 5. Config Inventory — links to Registry Explorer (DRR-111) ── */}
        <Card withBorder p="md">
          <Group gap="xs" mb="sm">
            <IconDatabase size={18} />
            <Text fw={600}>Config Type Registry</Text>
            <Badge size="xs" variant="light">Backend-Driven</Badge>
          </Group>
          <Text size="sm" c="dimmed" mb="md">
            Config types, dimensions, filename abbreviations, and the interactive filename decoder
            are now served from the backend registry.
          </Text>
          <Button
            variant="light"
            leftSection={<IconDatabase size={16} />}
            onClick={() => navigate('/architect/registries')}
          >
            Open Registry Explorer
          </Button>
        </Card>

        {/* ── 6. Quick Actions ── */}
        <Card withBorder p="md">
          <Group gap="xs" mb="sm">
            <IconRocket size={18} />
            <Text fw={600}>Quick Actions</Text>
          </Group>
          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
            <Button variant="light" leftSection={<IconTopologyStar3 size={16} />} onClick={() => navigate('/architect/federation')}>
              Federation
            </Button>
            <Button variant="light" leftSection={<IconSettings size={16} />} onClick={() => navigate('/architect/primitives')}>
              Primitives
            </Button>
            <Button variant="light" leftSection={<IconEye size={16} />} onClick={() => navigate('/architect/explorer')}>
              Explorer
            </Button>
            <Button variant="light" leftSection={<IconShieldCheck size={16} />} onClick={() => navigate('/architect/form-editor')}>
              Form Editor
            </Button>
          </SimpleGrid>
          <Divider my="sm" />
          <Group gap="xs">
            <Text size="xs" c="dimmed">Environment:</Text>
            <Badge size="xs" variant="outline">Mock Auth</Badge>
            <Badge size="xs" variant="outline">Tenant: {tenantId}</Badge>
            <Badge size="xs" variant="outline">Layer: {viewLayer}</Badge>
          </Group>
        </Card>
      </Stack>
    </Container>
  );
}
